import { useState, useRef, useEffect } from 'react';
import { Upload, X, Check, Loader2, FolderOpen } from 'lucide-react';
import { api, type AssetOwnerType, type AssetType } from '../lib/api';

interface AssetUploaderProps {
  ownerType: AssetOwnerType;
  ownerId: string;
  assetType?: AssetType;
  folders?: string[];
  currentFolder?: string;
  accept?: string;
  maxSizeMb?: number;
  onUploaded?: (assetId: string) => void;
  className?: string;
  label?: string;
  autoUpload?: boolean;
}

type UploadState = 'idle' | 'uploading' | 'registering' | 'done' | 'error';

const ASSET_TYPE_OPTIONS: { value: AssetType; label: string }[] = [
  { value: 'logo', label: 'Logo' },
  { value: 'banner', label: 'Banner' },
  { value: 'avatar', label: 'Avatar' },
];

export function AssetUploader({
  ownerType, ownerId, assetType: fixedAssetType, folders = [], currentFolder,
  accept = 'image/png,image/jpeg,image/webp',
  maxSizeMb = 5, onUploaded, className = '', label, autoUpload = false,
}: AssetUploaderProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [assetName, setAssetName] = useState('');
  const [selectedType, setSelectedType] = useState<AssetType>(fixedAssetType || 'logo');
  const [selectedFolder, setSelectedFolder] = useState(currentFolder || '');
  const [newFolder, setNewFolder] = useState('');
  const assetType = fixedAssetType || selectedType;
  const [preview, setPreview] = useState<string | null>(null);
  const [state, setState] = useState<UploadState>('idle');
  const [error, setError] = useState('');

  const effectiveGroup = newFolder || selectedFolder;

  const handleFile = (f: File) => {
    if (f.size > maxSizeMb * 1024 * 1024) {
      setError(`File too large. Maximum ${maxSizeMb}MB.`);
      return;
    }
    if (!accept.split(',').some(t => f.type === t.trim())) {
      setError('Invalid file type.');
      return;
    }
    setFile(f);
    setAssetName(f.name.replace(/\.[^.]+$/, ''));
    setPreview(URL.createObjectURL(f));
    setError('');
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const upload = async () => {
    if (!file) return;
    try {
      setState('uploading');
      const { uploadUrl, assetId, s3Key } = await api.getAssetUploadUrl({
        ownerType, ownerId, assetType, contentType: file.type, fileSize: file.size,
        group: effectiveGroup || undefined,
      });
      // Guard: never proceed to register with an incomplete presign. Without this,
      // a malformed presign response cascaded into the developer-facing backend
      // error "Missing required fields: assetId, s3Key…" landing in front of users.
      if (!uploadUrl || !assetId || !s3Key) {
        throw new Error(`Upload prep returned an incomplete response (uploadUrl:${!!uploadUrl} assetId:${!!assetId} s3Key:${!!s3Key}).`);
      }

      const res = await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
      if (!res.ok) throw new Error('Upload failed');

      setState('registering');
      await api.registerAsset({
        assetId, s3Key, ownerType, ownerId, assetType, contentType: file.type, sizeBytes: file.size,
        name: assetName || file.name,
        group: effectiveGroup || undefined,
      });

      setState('done');
      onUploaded?.(assetId);
      setTimeout(() => { setState('idle'); setFile(null); setPreview(null); setAssetName(''); setNewFolder(''); }, 2500);
    } catch (err) {
      setState('error');
      console.error('[asset-upload] prepare failed', err);
      setError((err as Error).message || 'Upload failed');
    }
  };

  // Auto-save mode: upload the instant a valid file is chosen — no separate button.
  useEffect(() => {
    if (autoUpload && file && state === 'idle') void upload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  const clear = () => {
    setFile(null);
    setPreview(null);
    setState('idle');
    setError('');
    setNewFolder('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const showFolderPicker = folders.length > 0 || currentFolder !== undefined;

  return (
    <div className={className}>
      {label && (
        <p className="mb-2 font-display text-[10px] font-800 uppercase tracking-[0.18em] text-iron-500">{label}</p>
      )}

      {!file ? (
        <div
          onDragOver={e => e.preventDefault()}
          onDrop={handleDrop}
          onClick={() => fileRef.current?.click()}
          className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-iron-600 bg-iron-950 p-6 transition-colors hover:border-forge-500/50 hover:bg-iron-900"
        >
          <Upload className="h-6 w-6 text-iron-500" />
          <p className="text-sm text-iron-400">Drop an image or click to browse</p>
          <p className="text-xs text-iron-500">PNG, JPG, WebP up to {maxSizeMb}MB</p>
        </div>
      ) : (
        <div className="relative rounded-xl border border-iron-700 bg-iron-950 p-4 space-y-3">
          <div className="flex items-center gap-4">
            {preview && (
              <img src={preview} alt="Preview" className="h-16 w-16 rounded-lg object-cover shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex gap-2 mb-1">
                <input
                  type="text"
                  value={assetName}
                  onChange={e => setAssetName(e.target.value)}
                  placeholder="Asset name"
                  className="input flex-1 rounded-lg text-sm py-1.5 px-2"
                />
                {!fixedAssetType && (
                  <select
                    value={selectedType}
                    onChange={e => setSelectedType(e.target.value as AssetType)}
                    className="input rounded-lg text-sm py-1.5 px-2 w-24"
                  >
                    {ASSET_TYPE_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                )}
              </div>
              <p className="text-xs text-iron-500">{file.name} &middot; {(file.size / 1024).toFixed(0)}KB</p>
            </div>
          </div>

          {/* Folder picker */}
          {showFolderPicker && (
            <div className="flex items-center gap-2">
              <FolderOpen className="h-4 w-4 text-iron-500 shrink-0" />
              <select
                value={selectedFolder}
                onChange={e => { setSelectedFolder(e.target.value); setNewFolder(''); }}
                className="input flex-1 rounded-lg text-xs py-1.5 px-2"
              >
                <option value="">Root (no folder)</option>
                {folders.map(f => (
                  <option key={f} value={f}>{f}</option>
                ))}
                <option value="__new__">+ New folder...</option>
              </select>
              {selectedFolder === '__new__' && (
                <input
                  type="text"
                  value={newFolder}
                  onChange={e => setNewFolder(e.target.value)}
                  placeholder="Folder name"
                  className="input flex-1 rounded-lg text-xs py-1.5 px-2"
                  autoFocus
                />
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-2">
            <button onClick={clear} className="rounded-lg p-2 text-iron-500 hover:bg-iron-800 hover:text-iron-300">
              <X className="h-4 w-4" />
            </button>
            {state === 'idle' && !autoUpload && (
              <button onClick={() => upload()} className="ember rounded-lg px-4 py-2 font-display text-xs font-800 uppercase tracking-wider">
                Upload{effectiveGroup ? ` to ${effectiveGroup.split('/').pop()}` : ''}
              </button>
            )}
            {(state === 'uploading' || state === 'registering') && (
              <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-forge-300">
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.5} /> Saving…
              </span>
            )}
            {state === 'done' && (
              <span className="flex items-center gap-1.5 rounded-md bg-emerald-500/15 px-2.5 py-1 text-xs font-bold uppercase tracking-wider text-emerald-300">
                <Check className="h-4 w-4" strokeWidth={2.5} /> Saved
              </span>
            )}
          </div>
        </div>
      )}

      {error && <p className="mt-2 text-xs text-red-400">{error}</p>}

      <input ref={fileRef} type="file" accept={accept} className="hidden" onChange={e => {
        const f = e.target.files?.[0];
        if (f) handleFile(f);
      }} />
    </div>
  );
}
